import { useCallback } from 'preact/hooks';
import dayjs from 'dayjs';
import getCalendarRanges from '../../../utils/getCalendarRanges';
import stringToDate from '../../../utils/stringToDate';
import { trackCalendarButtonClick } from '../utils/tracking';
import { setPickerProperty, setPickerView } from '../utils/pickerHelpers';

/**
 * Hook to manage all event handlers for the datepicker
 * Handles user interactions and picker manipulations
 */
const usePickerEventHandlers = ({
  $inputRef,
  field,
  openTicketText,
  maxDaysSearch,
  firstAvailableDate,
  dateFormat,
  onClose,
  onSelect,
  compactDateUsedRef,
  calendarButtonClickedRef,
  showCompactDate,
}) => {
  /**
   * Handle departure date change event
   * Updates the min date for the return picker
   */
  const handleChangeDepartureDate = useCallback((_, value) => {
    const $input = $inputRef.current;
    if (!$input) return;

    // Open ticket is a label, not a date, so it is matched before parsing. Parsing first would
    // discard it: stringToDate returns null for it on the dd-mmm-yy and mmm-dd-yy formats.
    if (value === openTicketText) {
      // If the value is open ticket, set min date to be calendar ranges
      const { min } = getCalendarRanges(maxDaysSearch, firstAvailableDate);
      setPickerProperty($input, 'min', min);
      return;
    }

    const departureDate = stringToDate(value, dateFormat);

    if (!departureDate) return;

    const newMin = [departureDate.getFullYear(), departureDate.getMonth(), departureDate.getDate()];
    setPickerProperty($input, 'min', newMin);
  }, [$inputRef, dateFormat, openTicketText, maxDaysSearch, firstAvailableDate]);

  /**
   * Handle return date change event
   * Updates the max date for the departure picker
   */
  const handleChangeReturnDate = useCallback((_, value) => {
    const $input = $inputRef.current;
    if (!$input) return;

    // If the value is open ticket, set max date to be calendar ranges. Matched before parsing for
    // the same reason as the departure handler above.
    if (value === openTicketText) {
      const { max } = getCalendarRanges(maxDaysSearch, firstAvailableDate);
      setPickerProperty($input, 'max', max);
      return;
    }

    const returnDate = stringToDate(value, dateFormat);

    if (!returnDate) return;

    const newMax = [returnDate.getFullYear(), returnDate.getMonth(), returnDate.getDate()];
    setPickerProperty($input, 'max', newMax);
  }, [$inputRef, dateFormat, openTicketText, maxDaysSearch, firstAvailableDate]);

  /**
   * Handle clean/clear button click
   * Clears the datepicker value
   */
  const handleClean = useCallback((e) => {
    const $input = $inputRef.current;
    if (!$input) return;

    e.stopPropagation(); // Avoid propagation to the field, this avoids the calendar to be opened

    if (onClose) onClose('');
    $input.val('');
    const openCalendar = !e.keepCalendarClosed && field === 'departureDate';
    compactDateUsedRef.current = false;
    
    if (openCalendar) {
      $input.click();
    }
  }, [$inputRef, field, onClose, compactDateUsedRef]);

  /**
   * Handle alternative date clicks (today/tomorrow buttons)
   * Sets the date and tracks the event
   */
  const handleAlternativeDatesClick = useCallback((type) => () => {
    const $input = $inputRef.current;
    if (!$input) return;

    calendarButtonClickedRef.current = type;
    const todayDate = dayjs();
    const picker = $input.pickadate('picker');

    if (type === 'today') {
      const selectedDate = todayDate.toDate();
      picker.set('select', selectedDate);
    }
    if (type === 'tomorrow') {
      const selectedDate = todayDate.add(1, 'day').toDate();
      picker.set('select', selectedDate);
    }
    
    // Close the picker explicitly to ensure it closes when already open
    picker.close();
    
    if (onSelect) onSelect(type);

    /**
     * This timeout is necessary to avoid a timing issue with pickadate.
     * When using set('select', selectedDate) and close(), pickadate triggers
     * the onClose event configured during initialization.
     * 
     * This causes onClose to be called twice:
     * 1. From pickadate's onClose event (with empty value '')
     * 2. From here with the correct value
     * 
     * By using setTimeout(0), we ensure our onClose call happens after
     * pickadate's automatic call, overriding the state with the correct value.
     */
    setTimeout(() => {
      if (onClose) onClose($input.val(), type);
    }, 0);

    trackCalendarButtonClick(type);
    compactDateUsedRef.current = true;
  }, [$inputRef, onClose, onSelect, calendarButtonClickedRef, compactDateUsedRef, field]);

  /**
   * Handle date button click
   * Opens the datepicker if conditions are met
   */
  const handleOnDateButtonClick = useCallback((e) => {
    e.stopPropagation(); // Prevent event from bubbling to parent wrapper
    const $input = $inputRef.current;
    if (!$input) return;

    calendarButtonClickedRef.current = '';

    if (showCompactDate) {
      trackCalendarButtonClick('choose');
      $input.click();
    }
  }, [$inputRef, calendarButtonClickedRef, showCompactDate]);

  /**
   * Handle label click
   * Opens the datepicker
   * This was done because there are cases where two widgets are in the same page
   */
  const handleOnLabelClick = useCallback(() => {
    const $input = $inputRef.current;
    if (!$input) return;

    calendarButtonClickedRef.current = '';
    $input.click();
  }, [$inputRef, calendarButtonClickedRef]);

  /**
   * Handle Mundial button click
   * Opens calendar and sets view to June 2026
   */
  const handleMundialClick = useCallback((e) => {
    e.stopPropagation(); // Prevent event from bubbling to parent wrapper
    const $input = $inputRef.current;
    if (!$input) return;

    calendarButtonClickedRef.current = 'mundial';

    // June 2026: month index 5 (0-based), year 2026
    const mundialDate = new Date(2026, 5, 1); // June 1, 2026
    const today = new Date();
    
    // Only set to June 2026 if current date is before June 2026
    if (today < mundialDate) {
      setPickerView({ $input, date: mundialDate, options: { openIfClosed: true } });
    }

    trackCalendarButtonClick('mundial');
  }, [$inputRef, calendarButtonClickedRef]);

  return {
    handleChangeDepartureDate,
    handleChangeReturnDate,
    handleClean,
    handleAlternativeDatesClick,
    handleOnDateButtonClick,
    handleOnLabelClick,
    handleMundialClick,
  };
};

export default usePickerEventHandlers;

